/**
 * Creates an autocomplete functionality for an input element
 * @param {HTMLInputElement} inputElement - The input element to attach autocomplete to
 * @param {Array} options - Array of options for autocomplete
 * @param {Function} onSelect - Callback function when an option is selected
 * @returns {Object} Autocomplete controller with hide method
 */
export function createAutocomplete(inputElement, options, onSelect = null) {
    let autocompleteList = null;
    let selectedIndex = -1;
  
    const showAutocomplete = (filteredOptions) => {
      hideAutocomplete();
  
      if (filteredOptions.length === 0) return;
  
      autocompleteList = document.createElement("div");
      autocompleteList.className = "autocomplete-list";
      autocompleteList.style.position = "absolute";
      autocompleteList.style.top = "100%";
      autocompleteList.style.left = "0";
      autocompleteList.style.right = "0";
      autocompleteList.style.background = "white";
      autocompleteList.style.border = "1px solid #ccc";
      autocompleteList.style.borderTop = "none";
      autocompleteList.style.borderRadius = "0 0 4px 4px";
      autocompleteList.style.maxHeight = "150px";
      autocompleteList.style.overflowY = "auto";
      autocompleteList.style.zIndex = "1000";
      autocompleteList.style.boxShadow = "0 2px 4px rgba(0,0,0,0.2)";
  
      filteredOptions.forEach((option, index) => {
        const optionElement = document.createElement("div");
        optionElement.className = "autocomplete-option";
        optionElement.textContent = option;
        optionElement.style.padding = "8px 12px";
        optionElement.style.cursor = "pointer";
        optionElement.style.borderBottom = "1px solid #eee";
        optionElement.style.fontSize = "14px";
  
        optionElement.addEventListener("mouseenter", () => {
          selectedIndex = index;
          updateSelection();
        });
  
        optionElement.addEventListener("click", () => {
          inputElement.value = option;
          inputElement.dispatchEvent(new Event("input"));
          hideAutocomplete();
          if (onSelect && typeof onSelect === "function") {
            onSelect(option);
          }
        });
  
        autocompleteList.appendChild(optionElement);
      });
  
      const container = inputElement.parentElement;
      container.style.position = "relative";
      container.appendChild(autocompleteList);
    };
  
    const hideAutocomplete = () => {
      if (autocompleteList) {
        autocompleteList.remove();
        autocompleteList = null;
      }
      selectedIndex = -1;
    };
  
    const updateSelection = () => {
      if (!autocompleteList) return;
  
      const items = autocompleteList.children;
      Array.from(items).forEach((item, index) => {
        if (index === selectedIndex) {
          item.style.backgroundColor = "#f0f0f0";
        } else {
          item.style.backgroundColor = "white";
        }
      });
    };
  
    inputElement.addEventListener("input", (e) => {
      const value = e.target.value.toLowerCase();
  
      if (value.length === 0) {
        hideAutocomplete();
        return;
      }
  
      const filteredOptions = options.filter((option) =>
        option.toLowerCase().includes(value)
      );
      showAutocomplete(filteredOptions);
    });
  
    inputElement.addEventListener("keydown", (e) => {
      if (!autocompleteList) return;
  
      const items = autocompleteList.children;
  
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          selectedIndex = Math.min(selectedIndex + 1, items.length - 1);
          updateSelection();
          break;
        case "ArrowUp":
          e.preventDefault();
          selectedIndex = Math.max(selectedIndex - 1, 0);
          updateSelection();
          break;
        case "Enter":
          e.preventDefault();
          if (selectedIndex >= 0 && items[selectedIndex]) {
            inputElement.value = items[selectedIndex].textContent;
            inputElement.dispatchEvent(new Event("input"));
            hideAutocomplete();
            if (onSelect && typeof onSelect === "function") {
              onSelect(items[selectedIndex].textContent);
            }
          }
          break;
        case "Escape":
          hideAutocomplete();
          break;
      }
    });
  
    inputElement.addEventListener("blur", () => {
      setTimeout(hideAutocomplete, 200);
    });
  
    return { hide: hideAutocomplete };
  }
  
  /**
   * Populates unique values from a layer field for autocomplete
   * @param {Object} layer - ArcGIS layer
   * @param {string} fieldName - Field name to get unique values from
   * @returns {Promise<Array>} Array of unique values
   */
  export async function populateAutocompleteOptions(layer, fieldName) {
    try {
      if (!layer) {
        console.error("Layer not available");
        return [];
      }
  
      await layer.when();
  
      const query = layer.createQuery();
      query.where = `${fieldName} IS NOT NULL AND ${fieldName} <> ''`;
      query.outFields = [fieldName];
      query.returnGeometry = false;
      query.returnDistinctValues = true;
      query.orderByFields = [fieldName];
  
      const response = await layer.queryFeatures(query);
      const valuesSet = new Set();
      
      response.features.forEach((feature) => {
        const value = feature.attributes[fieldName];
        if (value && value.trim()) {
          valuesSet.add(value.trim());
        }
      });
  
      return Array.from(valuesSet).sort();
    } catch (error) {
      console.error(`Error populating ${fieldName} options:`, error);
      return [];
    }
  }